/**
 * AuthProvider — echter Logto-Login (@logto/react) mit Dev-Modus-Weiche.
 *
 * VITE_AUTH_MODE=dev  → keine Logto-Abhängigkeit geladen, Dev-Identität
 *                        (admin, wie bisher der Platzhalter) steht sofort.
 * VITE_AUTH_MODE=logto (Default) → LogtoProvider + Login-Gate + /callback,
 *                        Muster 1:1 aus rmnc-personal/frontend/src/auth/.
 *
 * Konfiguration (Build-Zeit, siehe .env.example + Dockerfile-ARGs):
 *  - VITE_LOGTO_ENDPOINT      https://user.rmnc.app
 *  - VITE_LOGTO_APP_ID        App-ID der stammdaten-{stage,prod}-SPA-App
 *  - VITE_LOGTO_API_RESOURCE  https://stammdaten-api.rmnc.app (== Backend LOGTO_AUDIENCE)
 *
 * Rollen: app:stammdaten:{admin,pflege,lesen,finanzen} kommen als Claim
 * "roles" im ID-Token (Custom-JWT, siehe docs/verbund/LOGTO_PROVISIONIERUNG.md
 * im Leitstand-Repo). Das Backend prüft sie unabhängig davon aus dem
 * Access-Token — die Frontend-Gates (z. B. Finanzen-Bereich) sind nur UX,
 * nie die eigentliche Durchsetzung.
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from "react"
import {
  LogtoProvider,
  UserScope,
  useHandleSignInCallback,
  useLogto,
  type LogtoConfig,
} from "@logto/react"
import { Navigate, useLocation, useNavigate } from "react-router-dom"
import { AUTH_MODE, setLogoutHandler, setReloginHandler, setTokenProvider } from "./authStore"
import {
  abmelden,
  accessToken,
  fehlendeKonfiguration,
  istAbmeldung,
  rollenAusClaims,
  userManager,
  type User as AkSitzung,
} from "./authentikClient"

export interface AuthUser {
  sub: string
  name: string
  roles: string[]
}

interface AuthContextValue {
  user: AuthUser | null
  isAuthenticated: boolean
  signIn: () => void
  signOut: () => void
}

const DEV_USER: AuthUser = {
  sub: "dev-admin",
  name: "Dev Admin",
  roles: ["admin"],
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  isAuthenticated: false,
  signIn: () => {},
  signOut: () => {},
})

export function useAuth() {
  return useContext(AuthContext)
}

export function hasRole(user: AuthUser | null, role: string): boolean {
  return user?.roles.includes(role) ?? false
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth()
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }
  return <>{children}</>
}

/**
 * Zusaetzliches Rollen-Gate ueber RequireAuth hinaus (z. B. Finanzen-Bereich:
 * <RequireAuth><RequireRole role="app:stammdaten:finanzen">...).
 * "admin" darf immer, analog zu den Backend-Permission-Sets. Reine UX-Sperre —
 * die eigentliche Durchsetzung passiert serverseitig (require_permission_set).
 */
export function RequireRole({ role, children }: { role: string; children: ReactNode }) {
  const { user } = useAuth()
  if (!hasRole(user, "admin") && !hasRole(user, role)) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-slate-100 p-6">
        <p className="text-sm text-slate-600">
          Kein Zugriff auf diesen Bereich — es fehlt die Rolle <code>{role}</code>.
        </p>
      </main>
    )
  }
  return <>{children}</>
}

/** Vollflächige Ladeanzeige während Login-Redirect/Callback. */
function AuthLoading({ text = "Anmeldung wird geprüft…" }: { text?: string }) {
  return (
    <main className="flex min-h-screen items-center justify-center bg-slate-100">
      <div className="flex flex-col items-center gap-3 text-slate-600">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-900 border-t-transparent" />
        <span className="text-sm">{text}</span>
      </div>
    </main>
  )
}

// =============================================================================
// DEV-MODUS
// =============================================================================

function DevAuthProvider({ children }: { children: ReactNode }) {
  const value: AuthContextValue = {
    user: DEV_USER,
    isAuthenticated: true,
    signIn: () => {},
    signOut: () => {},
  }
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// =============================================================================
// LOGTO-MODUS
// =============================================================================

const LOGTO_ENDPOINT = import.meta.env.VITE_LOGTO_ENDPOINT ?? ""
const LOGTO_APP_ID = import.meta.env.VITE_LOGTO_APP_ID ?? ""
const API_RESOURCE = import.meta.env.VITE_LOGTO_API_RESOURCE ?? ""

const RETURN_TO_KEY = "auth:returnTo"

const logtoConfig: LogtoConfig = {
  endpoint: LOGTO_ENDPOINT,
  appId: LOGTO_APP_ID,
  // API-Resource → Logto stellt ein JWT-Access-Token mit aud=API_RESOURCE aus
  resources: API_RESOURCE ? [API_RESOURCE] : [],
  scopes: [UserScope.Profile, UserScope.Roles],
}

function callbackUri(): string {
  // basis-bewusst: unter /stammdaten/ lautet der Ruecksprung
  // /stammdaten/callback — sonst landete er beim Portal
  return `${window.location.origin}${import.meta.env.BASE_URL}callback`
}

/**
 * Ziel nach dem Abmelden. MUSS in Logto als Post-Logout-URI registriert sein.
 * window.location.origin allein ergaebe im Pfad-Betrieb den Portal-Ursprung —
 * der ist fuer diese App nicht registriert (rmnc-plattform Entscheidung 010).
 */
function postLogoutUri(): string {
  return `${window.location.origin}${import.meta.env.BASE_URL}`
}

function ConfigError({ fehlend }: { fehlend: string[] }) {
  return (
    <main className="flex min-h-screen items-center justify-center bg-slate-100 p-6">
      <div className="max-w-md space-y-2 text-center">
        <h1 className="text-lg font-semibold text-slate-900">Anmeldung nicht konfiguriert</h1>
        <p className="text-sm text-slate-600">
          Der Build wurde ohne vollständige Anmelde-Konfiguration erstellt. Es fehlen:{" "}
          <code>{fehlend.join(", ")}</code>. Bitte an die Administration wenden.
        </p>
      </div>
    </main>
  )
}

function AuthError({ onRetry }: { onRetry: () => void }) {
  return (
    <main className="flex min-h-screen items-center justify-center bg-slate-100 p-6">
      <div className="max-w-md space-y-4 text-center">
        <h1 className="text-lg font-semibold text-slate-900">Anmeldung fehlgeschlagen</h1>
        <p className="text-sm text-slate-600">
          Die Anmeldung wurde abgebrochen oder konnte nicht abgeschlossen werden. Bitte
          versuchen Sie es erneut — bei wiederholten Problemen an die Administration wenden.
        </p>
        <button
          type="button"
          onClick={onRetry}
          className="inline-flex items-center rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700"
        >
          Erneut anmelden
        </button>
      </div>
    </main>
  )
}

/** /callback: Code gegen Tokens tauschen, dann zurück zur gemerkten Seite. */
function CallbackPage() {
  const navigate = useNavigate()
  const { signIn } = useLogto()
  const { error } = useHandleSignInCallback(() => {
    const returnTo = sessionStorage.getItem(RETURN_TO_KEY) ?? "/"
    sessionStorage.removeItem(RETURN_TO_KEY)
    navigate(returnTo, { replace: true })
  })

  if (error) {
    return (
      <AuthError
        onRetry={() => {
          void signIn(callbackUri())
        }}
      />
    )
  }
  return <AuthLoading text="Anmeldung wird abgeschlossen…" />
}

/** Login-Gate: erzwingt Anmeldung, lädt User-Info+Rollen, registriert Token-/Re-Login-/Logout-Handler. */
function LogtoGate({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading, error, signIn, signOut, getAccessToken, getIdTokenClaims } =
    useLogto()
  const location = useLocation()
  const navigate = useNavigate()
  const [user, setUser] = useState<AuthUser | null>(null)
  const [claimsLoaded, setClaimsLoaded] = useState(false)
  const isCallback = location.pathname === "/callback"

  // Token-Provider: @logto/react refresht selbstständig (Refresh-Token)
  useEffect(() => {
    setTokenProvider(async () => (await getAccessToken(API_RESOURCE)) ?? null)
    return () => setTokenProvider(null)
  }, [getAccessToken])

  // Re-Login (401 nach abgelaufener Session) + Logout
  useEffect(() => {
    setReloginHandler(() => {
      sessionStorage.setItem(RETURN_TO_KEY, location.pathname + location.search)
      return signIn(callbackUri())
    })
    setLogoutHandler(() => signOut(postLogoutUri()))
    return () => {
      setReloginHandler(null)
      setLogoutHandler(null)
    }
  }, [signIn, signOut, location.pathname, location.search])

  // Nicht angemeldet → Login-Redirect (außer auf /callback und /login)
  useEffect(() => {
    if (isLoading || isAuthenticated || isCallback || error) return
    if (location.pathname === "/login") return
    sessionStorage.setItem(RETURN_TO_KEY, location.pathname + location.search)
    void signIn(callbackUri())
  }, [isLoading, isAuthenticated, isCallback, error, signIn, location.pathname, location.search])

  // Angemeldet → User-Info (sub/name/roles) aus den ID-Token-Claims laden
  useEffect(() => {
    if (!isAuthenticated) {
      setClaimsLoaded(false)
      return
    }
    let cancelled = false
    void (async () => {
      const claims = await getIdTokenClaims()
      if (cancelled || !claims) return
      const roles = (claims as { roles?: unknown }).roles
      setUser({
        sub: claims.sub,
        name: claims.name ?? claims.username ?? claims.sub,
        roles: Array.isArray(roles) ? roles.map(String) : [],
      })
      setClaimsLoaded(true)
    })()
    return () => {
      cancelled = true
    }
  }, [isAuthenticated, getIdTokenClaims])

  if (isCallback) return <CallbackPage />

  if (error) {
    return (
      <AuthError
        onRetry={() => {
          sessionStorage.setItem(RETURN_TO_KEY, location.pathname + location.search)
          void signIn(callbackUri())
        }}
      />
    )
  }

  const value: AuthContextValue = {
    user,
    isAuthenticated: isAuthenticated && claimsLoaded,
    signIn: () => {
      sessionStorage.setItem(RETURN_TO_KEY, location.pathname + location.search)
      void signIn(callbackUri())
    },
    signOut: () => {
      void signOut(postLogoutUri())
    },
  }

  // /login-Route: direkt weiter, sobald angemeldet
  if (location.pathname === "/login" && isAuthenticated && claimsLoaded) {
    navigate("/", { replace: true })
  }

  if (!isCallback && location.pathname !== "/login" && (!isAuthenticated || !claimsLoaded)) {
    return <AuthLoading text={isLoading ? "Anmeldung wird geprüft…" : "Weiterleitung zur Anmeldung…"} />
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

function LogtoAuthProvider({ children }: { children: ReactNode }) {
  const fehlend = [
    !LOGTO_ENDPOINT && "VITE_LOGTO_ENDPOINT",
    !LOGTO_APP_ID && "VITE_LOGTO_APP_ID",
    !API_RESOURCE && "VITE_LOGTO_API_RESOURCE",
  ].filter(Boolean) as string[]
  if (fehlend.length > 0) {
    return <ConfigError fehlend={fehlend} />
  }
  return (
    <LogtoProvider config={logtoConfig}>
      <LogtoGate>{children}</LogtoGate>
    </LogtoProvider>
  )
}

// =============================================================================
// AUTHENTIK-MODUS (Entscheidung 011)
// =============================================================================

/** /callback: Code gegen Tokens tauschen, dann zurück zur gemerkten Seite. */
function AuthentikCallbackPage() {
  const navigate = useNavigate()
  const [fehler, setFehler] = useState(false)

  useEffect(() => {
    userManager()
      .signinRedirectCallback()
      .then(() => {
        const returnTo = sessionStorage.getItem(RETURN_TO_KEY) ?? "/"
        sessionStorage.removeItem(RETURN_TO_KEY)
        navigate(returnTo, { replace: true })
      })
      .catch(() => setFehler(true))
  }, [navigate])

  if (fehler) {
    return <AuthError onRetry={() => void userManager().signinRedirect()} />
  }
  return <AuthLoading text="Anmeldung wird abgeschlossen…" />
}

/**
 * Login-Gate gegen Authentik — Vertrag identisch zum LogtoGate: erzwingt die
 * Anmeldung, lädt User-Info und Rollen, registriert Token-/Re-Login-/
 * Logout-Handler. Der OIDC-Kern steht in authentikClient.ts.
 */
function AuthentikGate({ children }: { children: ReactNode }) {
  const location = useLocation()
  const navigate = useNavigate()
  const [sitzung, setSitzung] = useState<AkSitzung | null>(null)
  const [geladen, setGeladen] = useState(false)
  const isCallback = location.pathname === "/callback"

  // Token-Provider für api.ts; oidc-client-ts erneuert selbstständig
  useEffect(() => {
    setTokenProvider(accessToken)
    return () => setTokenProvider(null)
  }, [])

  // Re-Login (401 nach abgelaufener Session) + Logout
  useEffect(() => {
    setReloginHandler(() => {
      sessionStorage.setItem(RETURN_TO_KEY, location.pathname + location.search)
      return userManager().signinRedirect()
    })
    setLogoutHandler(() => abmelden())
    return () => {
      setReloginHandler(null)
      setLogoutHandler(null)
    }
  }, [location.pathname, location.search])

  // Sitzung laden; Login-/Renew-/Logout-Ereignisse in den State
  useEffect(() => {
    const um = userManager()
    const beiLogin = (u: AkSitzung) => setSitzung(u)
    const beiLogout = () => setSitzung(null)
    um.events.addUserLoaded(beiLogin)
    um.events.addUserUnloaded(beiLogout)
    void um.getUser().then((u) => {
      setSitzung(u && !u.expired ? u : null)
      setGeladen(true)
    })
    return () => {
      um.events.removeUserLoaded(beiLogin)
      um.events.removeUserUnloaded(beiLogout)
    }
  }, [])

  // Nicht angemeldet → Login-Redirect (außer auf /callback und /login,
  // und nie während des Abmeldens)
  useEffect(() => {
    if (isCallback || !geladen || sitzung || istAbmeldung()) return
    if (location.pathname === "/login") return
    sessionStorage.setItem(RETURN_TO_KEY, location.pathname + location.search)
    void userManager().signinRedirect()
  }, [isCallback, geladen, sitzung, location.pathname, location.search])

  if (isCallback) return <AuthentikCallbackPage />

  const user: AuthUser | null = sitzung
    ? {
        sub: String((sitzung.profile as Record<string, unknown>).sub ?? ""),
        name: String(
          (sitzung.profile as Record<string, unknown>).name ??
            (sitzung.profile as Record<string, unknown>).preferred_username ??
            (sitzung.profile as Record<string, unknown>).sub ??
            "",
        ),
        roles: rollenAusClaims(sitzung.profile as Record<string, unknown>),
      }
    : null

  const value: AuthContextValue = {
    user,
    isAuthenticated: user !== null,
    signIn: () => {
      sessionStorage.setItem(RETURN_TO_KEY, location.pathname + location.search)
      void userManager().signinRedirect()
    },
    signOut: () => abmelden(),
  }

  // /login-Route: direkt weiter, sobald angemeldet
  if (location.pathname === "/login" && user) {
    navigate("/", { replace: true })
  }

  if (!isCallback && location.pathname !== "/login" && !user) {
    return <AuthLoading text={geladen ? "Weiterleitung zur Anmeldung…" : "Anmeldung wird geprüft…"} />
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

function AuthentikAuthProvider({ children }: { children: ReactNode }) {
  const fehlend = fehlendeKonfiguration()
  if (fehlend.length > 0) {
    return <ConfigError fehlend={fehlend} />
  }
  return <AuthentikGate>{children}</AuthentikGate>
}

// =============================================================================
// WEICHE
// =============================================================================

export function AuthProvider({ children }: { children: ReactNode }) {
  if (AUTH_MODE === "dev") {
    return <DevAuthProvider>{children}</DevAuthProvider>
  }
  if (AUTH_MODE === "authentik") {
    return <AuthentikAuthProvider>{children}</AuthentikAuthProvider>
  }
  return <LogtoAuthProvider>{children}</LogtoAuthProvider>
}
